import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { useTaxRatesList } from "./use-tax-rates-list";
import { CatalogStatusBadge } from "./CatalogStatusBadge";
import { CreateTaxRateDialog } from "./CreateTaxRateDialog";
import { ArchiveTaxRateDialog } from "./ArchiveTaxRateDialog";
import type { StripeTaxRate } from "./types";

interface TaxRatesSectionProps {
  readonly canEdit: boolean;
}

export function TaxRatesSection({ canEdit }: TaxRatesSectionProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useTaxRatesList();
  const [createOpen, setCreateOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<StripeTaxRate | null>(null);

  return (
    <section data-testid="catalog-tax-rates-section" aria-labelledby="catalog-tax-rates-heading">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 id="catalog-tax-rates-heading" className="font-display text-xl">
          {t("catalog.taxRates.title")}
        </h2>
        {canEdit && (
          <Button data-testid="create-tax-rate-button" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("catalog.taxRates.actions.create")}
          </Button>
        )}
      </div>

      {isLoading && (
        <p
          data-testid="catalog-tax-rates-loading"
          className="font-sans text-sm text-muted-foreground"
        >
          {t("catalog.loading")}
        </p>
      )}

      {isError && (
        <div
          data-testid="catalog-tax-rates-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">
            {t("catalog.taxRates.error")}
          </p>
        </div>
      )}

      {data && data.length === 0 && (
        <div
          data-testid="catalog-tax-rates-empty"
          className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center"
        >
          <p className="font-sans text-sm font-medium text-foreground">
            {t("catalog.taxRates.emptyTitle")}
          </p>
          <p className="mt-1 font-sans text-sm font-normal text-muted-foreground">
            {t("catalog.taxRates.emptyBody")}
          </p>
        </div>
      )}

      {data && data.length > 0 && (
        <div data-testid="catalog-tax-rates-list" className="space-y-3">
          {data.map((rate) => (
            <article
              key={rate.id}
              data-testid={`tax-rate-row-${rate.id}`}
              className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-border bg-card p-4"
            >
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-sans text-base font-medium text-foreground">
                    {rate.displayName}
                  </h3>
                  <CatalogStatusBadge active={rate.active} testId={`tax-rate-status-${rate.id}`} />
                  <span className="font-sans text-xs text-muted-foreground">
                    {rate.inclusive
                      ? t("catalog.taxRates.inclusive")
                      : t("catalog.taxRates.exclusive")}
                  </span>
                </div>
                <p className="font-sans text-sm text-muted-foreground">
                  {t("catalog.taxRates.fields.percentage")}: {rate.percentage}%
                </p>
                {rate.jurisdiction && (
                  <p className="font-sans text-sm text-muted-foreground">
                    {t("catalog.taxRates.fields.jurisdiction")}: {rate.jurisdiction}
                  </p>
                )}
                <p className="font-mono text-xs text-muted-foreground">{rate.id}</p>
              </div>
              {canEdit && rate.active && (
                <Button
                  data-testid={`archive-tax-rate-${rate.id}`}
                  variant="outline"
                  size="sm"
                  onClick={() => setArchiveTarget(rate)}
                >
                  {t("catalog.taxRates.actions.archive")}
                </Button>
              )}
            </article>
          ))}
        </div>
      )}

      <CreateTaxRateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={() => setCreateOpen(false)}
      />
      <ArchiveTaxRateDialog
        rate={archiveTarget}
        open={archiveTarget !== null}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
        onSuccess={() => setArchiveTarget(null)}
      />
    </section>
  );
}
